import { NgIf } from '@angular/common';
import { Component, inject, OnInit } from '@angular/core';
import { ValidationErrors } from '@angular/forms';
import { MatIconButton, MatMiniFabButton } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';
import { MatCell, MatCellDef, MatColumnDef, MatHeaderCell, MatHeaderCellDef, MatHeaderRow, MatHeaderRowDef, MatRow, MatRowDef, MatTable } from '@angular/material/table';
import { HaftpflichtSchaden } from '@gsm/entities';
import { DynamicFormControlModel } from '@ng-dynamic-forms/core';
import { CustomDynamicFormService } from '../../custom-dynamic-form.service';
import { getDisplayName } from '../../helpers/display-name.helper';
import { filterEmptyProps } from '../../helpers/filter-props.helper';
import { getGroupValue } from '../../helpers/get-group-value.helper';
import { CustomDynamicFormControlComponent } from '../custom-dynamic-form-control.component';
import { DokumentenAngabeModel } from '../dokumenten-angabe/dokumenten-angabe.model';
import { GeschaedigtePersonDialogData, HaftpflichtGeschaedigtePersonDialogComponent } from '../haftpflicht-geschaedigte-person/haftpflicht-geschaedigte-person-dialog/haftpflicht-geschaedigte-person-dialog.component';
import { HaftpflichtGeschaedigtePersonModel } from '../haftpflicht-geschaedigte-person/haftpflicht-geschaedigte-person.model';
import { AnyFormControlModel } from '../shared-models/any-form-control.model';
import { HaftpflichtSchadenVorhanden } from '../shared-models/haftpflichtschaden.model';
import { HAFTPFLICHT_SACHSCHADEN_HANDY_TEMPLATE } from './haftpflicht-sachschaden-handy.config';
import { HaftpflichtSachschadenHandyModel } from './haftpflicht-sachschaden-handy.model';

// NOTE:
// The base class CustomDynamicFormControlComponent already includes all inputs and outputs needed, as well
// as additional convenience methods onDisabledChange() and onValueChange, for when model.disabled and model.value change,
// respectively.

@Component({
  selector:    'gsm-haftpflicht-sachschaden-handy',
  templateUrl: 'haftpflicht-sachschaden-handy.component.html',
  styleUrls:   ['haftpflicht-sachschaden-handy.component.scss'],
  standalone:  true,
  imports:     [
    MatTable,
    MatColumnDef,
    MatHeaderCellDef,
    MatHeaderCell,
    MatCellDef,
    MatCell,
    MatIconButton,
    MatIcon,
    NgIf,
    MatHeaderRowDef,
    MatHeaderRow,
    MatRowDef,
    MatRow,
    MatMiniFabButton
  ]
})
export class HaftpflichtSachschadenHandyComponent extends CustomDynamicFormControlComponent<HaftpflichtSachschadenHandyModel>
  implements OnInit {

  private dialog = inject(MatDialog);
  private formService = inject(CustomDynamicFormService);

  constructor() {
    super();
  }

  ngOnInit() {
    super.ngOnInit();
  }

  protected onModelChange() {
    if (!this.model.value) {
      this.model.value = [];
    }
    this._emitChange();
  }

  private _emitChange() {
    this.group.get(this.model.id)
      .setErrors(this._getErrors());
    this.change.emit(this.model.value);
  }

  private _getErrors(): ValidationErrors {
    const isValid = this.model.value?.length;
    return isValid ? null : {sachschadenHandy: true};
  }

  getName(schaden: HaftpflichtSchaden): string {
    return schaden.vorhanden === HaftpflichtSchadenVorhanden.Nachgebracht
      ? 'Wird nachgebracht'
      : schaden.geschaedigtePerson?.nachname
        ? getDisplayName([
          schaden.geschaedigtePerson.vorname,
          schaden.geschaedigtePerson.nachname
        ])
        : 'Unbekannt';
  }

  getAufstellung(schaden: HaftpflichtSchaden): string {
    const geraete = schaden.geschaedigtePerson?.geschaedigtesGeraetList || schaden.geschaedigtePerson?.geschaedigtesHaushaltsgeraetList;
    const nachgebrachteGeraete = geraete?.filter(geraet => geraet.status === 'N');
    return nachgebrachteGeraete?.length && nachgebrachteGeraete.length === geraete.length
      ? 'Wird nachgebracht'
      : nachgebrachteGeraete?.length
        ? 'Wird teilweise nachgebracht'
        : geraete?.length
          ? 'Geräteaufstellung wurde erstellt'
          : ''; // in case the entire person is nachgebracht
  }

  onAddClick() {
    this._editItem({}, null);
  }

  onEditClick(item: HaftpflichtSchaden, index: number) {
    this._editItem(item, index, true);
  }

  onRemoveClick(index: number) {
    this.model.value = this.model.value.filter((_, i) => i !== index);
    this._emitChange();
  }

  private _editItem(item: HaftpflichtSchaden, index: number | null, isEditEvent = false) {
    this.dialog
      .open<HaftpflichtGeschaedigtePersonDialogComponent, GeschaedigtePersonDialogData, AnyFormControlModel[]>(
        HaftpflichtGeschaedigtePersonDialogComponent,
        {
          // allow quick closing only in view mode
          disableClose: !this.model.disabled,
          minWidth:     '600px',
          data:         {
            formConfig:      HAFTPFLICHT_SACHSCHADEN_HANDY_TEMPLATE(item, this.model.value, this.model.id, this.model.additional, isEditEvent),
            heading:
                             this.model.id === 'KERN_HAFTPFLICHT_FF_geraeteschaeden'
                               ? 'Sachschaden Handy/Laptop/Kamera/elektr.Unterhaltungsmedien - Geschädigte Person'
                               : 'Sachschaden Brille, Haushalts-/Gartengeräte - Geschädigte Person',
            disabled:        this.model.disabled,
            parentComponent: this,
            onFormChange:    this._onFormChange
          }
        }
      )
      .afterClosed()
      .subscribe(result => {
        if (result) {
          const resultEntity = this._getValueFromForm(result);
          this._saveEditedItem(resultEntity, index);
        }
      });
  }

  private _saveEditedItem(item: HaftpflichtSchaden, index: number | null) {
    // index == null means that a new item should be added
    if (index !== null) {
      this.model.value = this.model.value.map((val, i) => (index === i ? item : val));
    } else {
      this.model.value = [
        ...this.model.value,
        item
      ];
    }
    this._emitChange();
  }

  // Needs to be an arrow function to preserve "this" context.
  private _onFormChange = (models: DynamicFormControlModel[], changeModel?: AnyFormControlModel) => {
    if (changeModel?.id === 'geschaedigtePerson') {
      const geschaedigtePerson = (changeModel as HaftpflichtGeschaedigtePersonModel).value?.geschaedigtePerson;

      // If geschaedigtePerson has a nachname, use it as a Zahlungsempfaenger.
      if (geschaedigtePerson?.nachname) {
        const {
          vorname,
          nachname
        } = geschaedigtePerson;

        // Only add to kunde list if someone with the same name isn't there yet.
        if (!this.model.additional.kundeList?.some(kunde => kunde.vorname === vorname && kunde.nachname === nachname)) {
          const newKundeList = [
            ...(this.model.additional.kundeList || []),
            {
              vorname,
              nachname
            }
          ];

          // Set kunden property on DokumentenAngabe component.
          const belegeDokumente = this.formService.findModelById<DokumentenAngabeModel>('aufstellungBelege', models);
          belegeDokumente.additional.kunden = newKundeList;
        }
      }
    }
  };

  private _getValueFromForm(formModel: AnyFormControlModel[]): HaftpflichtSchaden {
    const {
      geschaedigtePerson:            {
                                       id,
                                       vorhanden,
                                       geschaedigtePerson: person
                                     } = {} as any,
      geschaedigtesGeraetList_group: {geschaedigtesGeraetList} = {} as any,
      aufstellungBelege_group:       {aufstellungBelege: belege} = {} as any
    } = getGroupValue(formModel);
    const geraetListField = this.model.additional.haushalt ? 'geschaedigtesHaushaltsgeraetList' : 'geschaedigtesGeraetList';

    const rawObject = () => {
      return vorhanden !== 'N'
        ? {
          id,
          vorhanden,
          geschaedigtePerson: {
            ...person,
            [geraetListField]: geschaedigtesGeraetList
          },
          belege
        }
        : {
          id,
          vorhanden,
          belege
        };
    };

    return filterEmptyProps<HaftpflichtSchaden>(rawObject());
  }
}
